import { useQuery } from '@apollo/client';
import { message } from 'antd';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GET_BAG_TASK_LOG, GetBagTaskLogResponse, GetBagTaskLogVariables } from '../graphql/queries/getBagTaskLog';
import { BagTaskLogOperateStatus } from '../graphql/types/ConsolidationTypes';

interface UseBagTaskLogPollingOptions {
    bagTaskLogId?: number;
    onSuccess?: (data: GetBagTaskLogResponse['getBagTaskLog']) => void;
    onError?: (error: string) => void;
    pollingInterval?: number; // 轮询间隔，默认2秒
    maxPollingTime?: number;  // 最大轮询时间，默认5分钟
}

interface UseBagTaskLogPollingResult {
    data: GetBagTaskLogResponse['getBagTaskLog'] | null;
    loading: boolean;
    error: string | null;
    isPolling: boolean;
    startPolling: (bagTaskLogId: number) => void;
    stopPolling: () => void;
}

export const useBagTaskLogPolling = (options: UseBagTaskLogPollingOptions = {}): UseBagTaskLogPollingResult => {
    const { t } = useTranslation();
    const {
        bagTaskLogId,
        onSuccess,
        onError,
        pollingInterval = 2000, // 2秒
        maxPollingTime = 300000  // 5分钟
    } = options;

    const [isPolling, setIsPolling] = useState(false);
    const [currentBagTaskLogId, setCurrentBagTaskLogId] = useState<number | undefined>(bagTaskLogId);
    const pollingStartTimeRef = useRef<number | null>(null);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const { data, loading, error, startPolling: apolloStartPolling, stopPolling: apolloStopPolling } = useQuery<
        GetBagTaskLogResponse,
        GetBagTaskLogVariables
    >(GET_BAG_TASK_LOG, {
        variables: { id: currentBagTaskLogId! },
        skip: !currentBagTaskLogId,
        pollInterval: isPolling ? pollingInterval : 0,
        errorPolicy: 'all',
        fetchPolicy: 'network-only', // 强制每次都从网络获取，避免缓存影响
        onCompleted: (data) => {
            console.log('onCompleted triggered:', data?.getBagTaskLog);
            console.log('isPolling:', isPolling);
            // 状态检查逻辑移到了 useEffect 中处理
        },
        onError: (error) => {
            if (isPolling) {
                console.error(t('wms.baggingTask.messages.bagTaskLogPollingFailed'), error);
                const errorMsg = `${t('wms.baggingTask.messages.queryTaskStatusFailed')}: ${error.message}`;
                stopPolling();
                onError?.(errorMsg);
            }
        }
    });

    const startPolling = (bagTaskLogId: number) => {
        setCurrentBagTaskLogId(bagTaskLogId);
        setIsPolling(true);
        pollingStartTimeRef.current = Date.now();
        
        // 设置最大轮询时间超时
        timeoutRef.current = setTimeout(() => {
            stopPolling();
            const timeoutMsg = t('wms.baggingTask.messages.bagTaskTimeout');
            message.warning(timeoutMsg);
            onError?.(timeoutMsg);
        }, maxPollingTime);

        message.info(t('wms.baggingTask.messages.bagTaskStarted'));
    };

    const stopPolling = () => {
        setIsPolling(false);
        apolloStopPolling();
        
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
        pollingStartTimeRef.current = null;
    };

    // 清理定时器
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    // 监听数据变化，检查任务状态
    useEffect(() => {
        if (data?.getBagTaskLog && isPolling) {
            console.log('Data changed, checking status:', data.getBagTaskLog);
            const status = data.getBagTaskLog.operateStatus;

            // 如果任务完成（成功或失败），停止轮询
            if (status === BagTaskLogOperateStatus.SUCCESS || status === BagTaskLogOperateStatus.FAILED) {
                stopPolling();
                if (status === BagTaskLogOperateStatus.SUCCESS) {
                    onSuccess?.(data.getBagTaskLog);
                } else if (status === BagTaskLogOperateStatus.FAILED) {
                    onError?.(t('wms.baggingTask.messages.bagTaskExecutionFailed'));
                }
            }
        }
    }, [data?.getBagTaskLog?.operateStatus, isPolling, data?.getBagTaskLog?.updatedAt, onSuccess, onError]);

    // 监听bagTaskLogId变化
    useEffect(() => {
        if (bagTaskLogId && bagTaskLogId !== currentBagTaskLogId) {
            startPolling(bagTaskLogId);
        }
    }, [bagTaskLogId]);

    return {
        data: data?.getBagTaskLog || null,
        loading,
        error: error?.message || null,
        isPolling,
        startPolling,
        stopPolling
    };
};
